import React, { useEffect, useState, useCallback, useRef } from 'react';
import { PromoPopupCard } from './PromoPopupCard';
import { getPromoTheme } from './promoThemes';

// Clé par pop-up ET par canal : le portail et la vitrine partagent la même origine en preview
// (et un même visiteur peut fréquenter les deux) → chaque canal a sa propre fréquence.
const storageKey = (id, placement) => `promo_popup_${id}_${placement}`;
const sessionKey = (id, placement) => `promo_popup_session_${id}_${placement}`;
const DAY_MS = 24 * 60 * 60 * 1000;

const readState = (id, placement) => {
  try { return JSON.parse(localStorage.getItem(storageKey(id, placement)) || 'null') || {}; } catch { return {}; }
};
const writeState = (id, placement, patch) => {
  try { localStorage.setItem(storageKey(id, placement), JSON.stringify({ ...readState(id, placement), ...patch })); } catch {}
};

// Mode test admin via l'URL : `?popup=1` force le pop-up en ligne malgré la fréquence,
// `?popup=<id>` affiche ce pop-up précis (même programmé / inactif). Aucune stat n'est comptée.
export const getPopupTestParam = () => {
  try { return new URLSearchParams(window.location.search).get('popup') || ''; } catch { return ''; }
};
const isForced = () => !!getPopupTestParam();
const previewId = () => { const v = getPopupTestParam(); return v && v !== '1' ? v : ''; };

export const shouldShowPopup = (popup, placement = 'vitrine') => {
  if (!popup?.id) return false;
  if (isForced()) return true;
  const st = readState(popup.id, placement);
  if (st.never) return false;
  const freq = popup.popup_frequency || 'daily';
  if (freq === 'once') return !st.last;
  if (freq === 'daily') return !st.last || Date.now() - st.last > DAY_MS;
  try { return !sessionStorage.getItem(sessionKey(popup.id, placement)); } catch { return true; }
};

// Ciblage de pages vitrine : liste vide = tout le site
export const popupAllowedOnPage = (popup, placement, pageKey) => {
  if (placement !== 'vitrine' || !pageKey) return true;
  const pages = popup?.popup_vitrine_pages || [];
  return pages.length === 0 || pages.includes(pageKey);
};

/**
 * Pop-up promotionnel live.
 * - endpoint : URL complète de /promo-popup (vitrine ou portail)
 * - apiBase  : base pour les URLs relatives (image, tracking)
 * - pageKey  : page vitrine courante (home, restaurant, …) pour le ciblage par page
 * - onNavigate(url) : navigation gérée par l'hôte (vitrine / portail)
 */
export const PromoPopup = ({ endpoint, apiBase = '', authToken = null, placement = 'vitrine', pageKey = '', onNavigate, disabled = false, keepClickableSelector = null }) => {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const [topOffset, setTopOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 640px)').matches : false));
  const timerRef = useRef(null);
  const shownRef = useRef(false);
  const impressionRef = useRef(false);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, []);

  // Vitrine : l'overlay démarre sous le header fixe pour laisser le sélecteur de langue cliquable
  useEffect(() => {
    if (!visible || !keepClickableSelector) return undefined;
    const measure = () => {
      const el = document.querySelector(keepClickableSelector);
      setTopOffset(el ? Math.max(0, Math.round(el.getBoundingClientRect().bottom)) : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [visible, keepClickableSelector]);

  // 1) Chargement du pop-up éligible (fréquence) — l'affichage dépend ensuite de la page courante
  useEffect(() => {
    if (disabled || !endpoint) return undefined;
    let cancelled = false;
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    const pid = previewId();
    const url = pid ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}preview_id=${encodeURIComponent(pid)}` : endpoint;
    fetch(url, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const p = data?.popup;
        if (cancelled || !p) return;
        if (shownRef.current) { setPopup(p); return; } // changement de langue : on rafraîchit juste les textes
        if (!shouldShowPopup(p, placement)) return;
        setPopup(p);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [endpoint, authToken, disabled, placement]);

  // 2) Déclenchement (délai) dès que la page courante est ciblée — réévalué à chaque navigation vitrine
  useEffect(() => {
    if (!popup || shownRef.current || !popupAllowedOnPage(popup, placement, pageKey)) return undefined;
    timerRef.current = setTimeout(() => { shownRef.current = true; setVisible(true); }, Math.max(0, (popup.popup_delay_seconds || 0) * 1000));
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [popup, placement, pageKey]);

  const track = useCallback((kind) => {
    if (!popup?.id || isForced() || popup._preview) return; // mode test admin : pas de stats
    const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };
    fetch(`${apiBase}/api/featured-deals/${popup.id}/${kind}?source_hint=popup&placement=${encodeURIComponent(placement)}`, { method: 'POST', headers }).catch(() => {});
  }, [popup, apiBase, authToken, placement]);

  useEffect(() => {
    if (!visible || !popup || impressionRef.current) return;
    impressionRef.current = true;
    if (!isForced()) {
      writeState(popup.id, placement, { last: Date.now() });
      try { sessionStorage.setItem(sessionKey(popup.id, placement), '1'); } catch {}
    }
    track('impression');
  }, [visible, popup, track, placement]);

  if (!popup || !visible) return null;

  const theme = getPromoTheme(popup.popup_theme);
  const imageSrc = popup.popup_image_url
    ? `${apiBase}${popup.popup_image_url}`
    : (popup.popup_use_theme_image !== false ? theme.image : null);

  const close = () => setVisible(false);
  const dismissForever = () => { if (!isForced()) writeState(popup.id, placement, { never: true }); setVisible(false); };
  const cta = () => {
    track('click');
    setVisible(false);
    if (popup.popup_url && onNavigate) onNavigate(popup.popup_url, popup);
  };

  return (
    <div className={`promo-popup-overlay ${isMobile ? 'promo-popup--mobile' : ''}`} style={topOffset && !isMobile ? { top: topOffset } : undefined} onClick={close} data-testid="promo-popup-overlay">
      <div onClick={(e) => e.stopPropagation()} className="w-full flex justify-center">
        {popup._preview && (
          <span className="fixed top-3 left-1/2 -translate-x-1/2 z-[1] text-[11px] px-2.5 py-1 rounded-full bg-amber-400 text-amber-950 font-semibold shadow" data-testid="promo-popup-test-badge">
            Mode test — aucune statistique comptée
          </span>
        )}
        <PromoPopupCard popup={popup} imageSrc={imageSrc} onClose={close} onCta={cta} onDismissForever={dismissForever} />
      </div>
    </div>
  );
};

export default PromoPopup;
